/**
 * The animated line chart that plays over the revenue dashboard screenshot on
 * hero slide 2.
 *
 * The screenshot (`/v2/hero-macbook.png`, 2388x1776) already has the chart
 * baked in, so this draws over it: a white patch covers the baked plot area —
 * sampled and confirmed pure #FFFFFF, so the patch is seamless — and every
 * mark inside it is redrawn live. The card's title, y-axis labels, month
 * labels and legend are left alone, which is why the patch stops short of
 * them on all four sides.
 *
 * Everything below is in the screenshot's own pixel space. The <svg> carries
 * `viewBox="0 0 2388 1776"` and is stretched over the <Image>, so a coordinate
 * here is literally a pixel of the PNG and the overlay stays registered at any
 * rendered size. The numbers were measured off the file, not eyeballed: the
 * data points are the centroids of the coloured dots, the gridlines are the
 * rows that run non-white across the plot.
 */

/** Plot bounds inside the screenshot. */
const PLOT = {
  /** First gridline pixel; the Jan data point sits on it. */
  left: 806,
  /** The screenshot is cropped mid-chart, so the plot runs off its right edge. */
  right: 2388,
  /** Horizontal gridlines, top (axis max 2600) to bottom (zero). */
  grid: [1036, 1127, 1219, 1310, 1401],
};

/** Patch over the baked-in plot: clear of the axis labels (x < 800), the card
    heading above (ink ends ~1010) and the month labels below (ink from 1419). */
const PATCH = { x: 800, y: 1026, w: PLOT.right - 800, h: 388 };

/** Month tick positions, Jan-Aug. Measured; spacing is a hair over 214px. */
const MONTH_X = [807, 1028, 1242, 1455, 1668, 1882, 2095, 2308];
/** One tick past the crop. The curves have to keep going to leave the right
    edge at the angle the screenshot leaves it at, so each series carries a
    ninth value that is never dotted — it only shapes the exit. */
const OFF_CANVAS_X = 2522;

type Series = {
  key: string;
  /** Line colour — the pale tint the screenshot uses. */
  stroke: string;
  /** Dot colour — the saturated version of the same hue. */
  dot: string;
  /** Nine y values: Jan-Aug, then the off-canvas point. */
  y: number[];
  /** Dotted, like the screenshot's "all branches" reference line. */
  dotted?: boolean;
  /** Draw order offset, in seconds. */
  delay: number;
};

/** Bottom band first, so the lines stack up the way the eye reads them. */
const SERIES: Series[] = [
  {
    key: "siam",
    stroke: "#B1BDF4",
    dot: "#667EEA",
    y: [1360, 1336, 1331, 1356, 1331, 1367, 1286, 1269, 1000],
    delay: 0,
  },
  {
    key: "samut",
    stroke: "#EFE096",
    dot: "#E2CE5C",
    y: [1315, 1284, 1264, 1250, 1300, 1319, 1248, 1320, 1170],
    delay: 0.07,
  },
  {
    key: "rama9",
    stroke: "#C3E7BC",
    dot: "#39B70D",
    y: [1264, 1246, 1176, 1169, 1258, 1269, 1169, 1231, 1120],
    delay: 0.14,
  },
  {
    key: "total",
    stroke: "#9E9E9E",
    dot: "#111111",
    y: [1047, 1062, 1057, 1126, 1057, 1102, 1050, 1078, 1120],
    dotted: true,
    delay: 0.21,
  },
];

/**
 * Cardinal spline through the points, emitted as cubic Béziers.
 *
 * `k` is the tension. At the textbook 1/6 the curve is tame; the screenshot's
 * chart visibly overshoots between two near-equal peaks (the green line humps
 * down between Mar and Apr), which is what a looser spline does, so 0.21 is
 * tuned to reproduce that character rather than to be mathematically neutral.
 */
function splinePath(xs: number[], ys: number[], k = 0.21) {
  const p = (i: number) => {
    const j = Math.max(0, Math.min(xs.length - 1, i));
    return [xs[j], ys[j]] as const;
  };
  let d = `M ${xs[0]} ${ys[0]}`;
  for (let i = 0; i < xs.length - 1; i++) {
    const [x0, y0] = p(i - 1);
    const [x1, y1] = p(i);
    const [x2, y2] = p(i + 1);
    const [x3, y3] = p(i + 2);
    d +=
      ` C ${x1 + (x2 - x0) * k} ${y1 + (y2 - y0) * k}` +
      ` ${x2 - (x3 - x1) * k} ${y2 - (y3 - y1) * k}` +
      ` ${x2} ${y2}`;
  }
  return d;
}

const XS = [...MONTH_X, OFF_CANVAS_X];
const PATHS = SERIES.map((s) => ({ ...s, d: splinePath(XS, s.y) }));

/** Length of one series' draw, mirroring `hero-chart-draw` in globals.css, so
    the dot pops ride the draw front instead of trailing behind it. */
const DRAW = 0.9;

export default function RevenueChart() {
  return (
    <svg
      viewBox="0 0 2388 1776"
      className="hero-chart pointer-events-none absolute inset-0 h-full w-full"
      aria-hidden
    >
      {/* Hide the baked-in chart. Pure white on pure white — invisible. */}
      <rect x={PATCH.x} y={PATCH.y} width={PATCH.w} height={PATCH.h} fill="#fff" />

      {/* Gridlines, redrawn because the patch covered them. Horizontals span
          the plot; verticals sit on the month ticks, same as the screenshot. */}
      <g className="hero-chart-grid" stroke="#E5E5E5" strokeWidth={2.4} strokeDasharray="7 9">
        {PLOT.grid.map((y) => (
          <line key={y} x1={PLOT.left} y1={y} x2={PLOT.right} y2={y} />
        ))}
        {MONTH_X.map((x) => (
          <line key={x} x1={x} y1={PLOT.grid[0]} x2={x} y2={PLOT.grid.at(-1)!} />
        ))}
      </g>

      {/* Clip everything that follows to the plot, so the off-canvas ninth
          point and the sweep can't paint over the axis labels or the card. */}
      <clipPath id="hero-chart-clip">
        <rect x={PATCH.x} y={PATCH.y} width={PATCH.w} height={PATCH.h} />
      </clipPath>

      <g clipPath="url(#hero-chart-clip)">
        {PATHS.map((s) => (
          <g key={s.key} style={{ "--d": `${s.delay}s` } as React.CSSProperties}>
            {/* The dotted reference line can't use a dash-offset draw — its
                dash array is already spoken for by the dots themselves — so it
                fades up over the same window the others draw in. */}
            <path
              className={s.dotted ? "hero-chart-line-dotted" : "hero-chart-line"}
              d={s.d}
              fill="none"
              stroke={s.stroke}
              strokeWidth={s.dotted ? 6 : 7}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray={s.dotted ? "0.1 26" : undefined}
              pathLength={s.dotted ? undefined : 1}
            />
            {/* Second pass of the same path carrying a short bright dash that
                travels its length — the idle "live data" glint once the draw
                has settled. Solid lines only; over the dotted one it reads as
                noise. */}
            {!s.dotted && (
              <path
                className="hero-chart-glint"
                d={s.d}
                fill="none"
                stroke={s.dot}
                strokeWidth={7}
                strokeLinecap="round"
                pathLength={1}
              />
            )}
          </g>
        ))}

        {/* Dots last so they sit above every line. Each pops as the draw
            front reaches its month. */}
        {PATHS.map((s) =>
          MONTH_X.map((x, i) => (
            <circle
              key={`${s.key}-${x}`}
              className="hero-chart-dot"
              cx={x}
              cy={s.y[i]}
              r={7}
              fill={s.dot}
              stroke="#fff"
              strokeWidth={3.4}
              style={
                {
                  "--d": `${s.delay + 0.18 + (i / MONTH_X.length) * DRAW}s`,
                } as React.CSSProperties
              }
            />
          )),
        )}

        {/* The draw front: a soft vertical wash that rides across the plot
            once as the lines come in, then leaves. */}
        <rect
          className="hero-chart-sweep"
          x={PLOT.left - 60}
          y={PATCH.y}
          width={120}
          height={PATCH.h}
          fill="url(#hero-chart-sweep-grad)"
        />
      </g>

      <defs>
        <linearGradient id="hero-chart-sweep-grad" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%" stopColor="#2D6BF0" stopOpacity="0" />
          <stop offset="55%" stopColor="#2D6BF0" stopOpacity="0.18" />
          <stop offset="100%" stopColor="#2D6BF0" stopOpacity="0" />
        </linearGradient>
      </defs>
    </svg>
  );
}
